// Injects a single <style> element that overrides the status-pill colors for
// every tenant-configured status that carries a custom `color`. This avoids
// threading color into each StatusBadge / facility badge call site: the pills
// keep rendering `badge st-<code>` / `facility-st-<code>` classes, and these
// generated rules tint the matching codes app-wide.
//
// Mounted once inside the authed Layout. Resilient by design — any fetch error
// is swallowed and simply yields no overrides (the built-in per-code CSS wins).

import { useApi } from "../lib/useApi";
import { listStatuses, listTaskStatuses, listPriorities, getFacilityConfig } from "../lib/api";

/** Same normalization Badge.tsx uses for `st-`/`pr-` classes: lowercase, runs of
 *  non-alphanumerics collapsed to a single dash. */
function norm(code: string | null | undefined): string {
  return (code ?? "unknown").toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

/** hex → rgba() at the given alpha. Tolerates a missing "#" and 3-digit
 *  shorthand; returns the input unchanged if it isn't a valid hex (so a bad
 *  value degrades to "use the color literally" rather than breaking the rule). */
function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(h)) return hex;
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** One pill rule: soft-tinted background, colored text, slightly stronger tinted
 *  border — mirrors how AssetStatusBadge tints its inline style. */
function rule(selector: string, color: string): string {
  return `${selector}{background:${hexToRgba(color, 0.14)};color:${color};border-color:${hexToRgba(color, 0.35)};}`;
}

export function StatusColorStyles() {
  // Each fetch is independent and best-effort; a failure leaves that list empty.
  const ticket = useApi((signal) => listStatuses(signal), []);
  const task = useApi((signal) => listTaskStatuses(signal), []);
  const priority = useApi((signal) => listPriorities(signal), []);
  const facility = useApi((signal) => getFacilityConfig(signal), []);

  const rules: string[] = [];

  for (const s of ticket.data?.statuses ?? []) {
    if (s.color) rules.push(rule(`.badge.st-${norm(s.code)}`, s.color));
  }
  for (const s of task.data?.task_statuses ?? []) {
    if (s.color) rules.push(rule(`.badge.st-${norm(s.code)}`, s.color));
  }
  // Priority pills carry `badge pr-<code>` — tint the matching codes app-wide.
  for (const p of priority.data?.priorities ?? []) {
    if (p.color) rules.push(rule(`.badge.pr-${norm(p.code)}`, p.color));
  }
  // Facility badges use the RAW code in their class (e.g. `facility-st-in_progress`),
  // so match that here rather than the dashed norm() form.
  for (const o of facility.data?.config.statuses ?? []) {
    if (o.color) rules.push(rule(`.badge.facility-st-${o.code}`, o.color));
  }

  if (rules.length === 0) return null;
  return <style>{rules.join("")}</style>;
}
